"use client";

import { useQuery } from "@tanstack/react-query";

import { useAuthStore } from "@/modules/auth/stores/auth-store";

import { getWidget } from "../services/dashboard";

import type { WidgetDefinition } from "@/types";

// A slow widget's payload is expensive to produce — it leaves the server — so it
// is refetched less eagerly than a plain DB read. Fast widgets take the app-wide
// default (5 minutes, see query-provider).
const SLOW_STALE_TIME = 15 * 60 * 1000;

/**
 * One widget, one request, one cache entry.
 *
 * The whole point of the split: a card paints as soon as ITS query answers, its
 * retry button refetches only itself, and changing its date range invalidates
 * only its own data. The batch this replaced could do none of the three — every
 * card in a tier shared one request, one cache entry, and one params object, so a
 * single slow widget held up its whole tier and one date-range change refetched
 * everything in it.
 *
 * The key includes only THIS widget's params, so one card's controls never
 * invalidate another's.
 */
export function useWidget(
  definition: WidgetDefinition,
  params?: Record<string, unknown>,
) {
  const companyUuid = useAuthStore((s) => s.companyAccount?.uuid);
  const isSlow = definition.tier === "slow";

  const query = useQuery({
    queryKey: [
      "dashboard",
      "widget",
      companyUuid,
      definition.key,
      params ?? null,
    ],
    queryFn: () => getWidget(definition.key, params),
    enabled: !!companyUuid,
    staleTime: isSlow ? SLOW_STALE_TIME : undefined,
    // Only transport failures reach this — a widget that THREW answers 200 with
    // `status: 'error'`, which is data, not a rejection, and the card renders its
    // own retry for it. So retrying is worth it exactly where the transport is
    // the flaky part: an upstream call. A failed DB read is a bug, and hiding it
    // behind retries only delays the card's error state.
    retry: isSlow ? 1 : false,
  });

  return {
    ...query,
    // `null` is the 404 case — unknown key, or one this user may no longer see.
    // A normal empty state, not an error, so it reads as "no result" here too.
    result: query.data ?? undefined,
  };
}
